// lib/divider/MoveableDivider.tsx
import styled from "@emotion/styled";
import { useTheme } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
var Resizer = styled.div(
  ({ theme }) => `
  width: 4px;
  cursor: col-resize;
  background-color: transparent;
  transition: background-color 0.2s;
  z-index: 10;
  margin-left: -2px;
  margin-right: -2px;
  &:hover {
    background-color: ${theme.palette.primary.main};
    opacity: 0.5;
  }
  &:active {
    background-color: ${theme.palette.primary.main};
    opacity: 1;
  }
`
);
var MoveableDivider = ({
  defaultSize,
  minSize = 100,
  maxSize = 1e3,
  onSizeChange,
  containerRef
}) => {
  const theme = useTheme();
  const isResizing = useRef(false);
  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizing.current) return;
      const offset = containerRef?.current?.getBoundingClientRect().left ?? 0;
      const newSize = Math.max(minSize, Math.min(maxSize, e.clientX - offset));
      onSizeChange(newSize);
    },
    [minSize, maxSize, onSizeChange, containerRef]
  );
  const stopResizing = useCallback(() => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "default";
    document.body.style.userSelect = "auto";
  }, [handleMouseMove]);
  const startResizing = useCallback(() => {
    isResizing.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [handleMouseMove, stopResizing]);
  const resetSize = useCallback(() => {
    onSizeChange(defaultSize);
  }, [defaultSize, onSizeChange]);
  return /* @__PURE__ */ jsx(
    Resizer,
    {
      onMouseDown: startResizing,
      onDoubleClick: resetSize,
      theme
    }
  );
};
function useMoveableDivider({
  storageKey,
  defaultSize,
  minSize,
  maxSize,
  containerRef
}) {
  const [size, setSize] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved !== null ? parseInt(saved, 10) : defaultSize;
  });
  const handleSizeChange = useCallback(
    (newSize) => {
      setSize(newSize);
      localStorage.setItem(storageKey, newSize.toString());
    },
    [storageKey]
  );
  const divider = useMemo(
    () => /* @__PURE__ */ jsx(
      MoveableDivider,
      {
        defaultSize,
        minSize,
        maxSize,
        onSizeChange: handleSizeChange,
        containerRef
      }
    ),
    [defaultSize, minSize, maxSize, handleSizeChange, containerRef]
  );
  return [size, divider];
}
export {
  MoveableDivider,
  useMoveableDivider
};
